import axios from "axios";
import throttle from "lodash/throttle";
import {defineStore} from "pinia";
import {useConversation} from "./Conversation";
import {useWorkspace} from "./Workspaces";
import {useWorkspaceTree} from "./WorkspaceTree";
import {shouldSkipStateRestoration} from "../router";
import router from "../router";
import {RouteHelper} from "../Helpers/RouteHelper";
import {EntityResolver} from "../Services/EntityResolver";
import {EntityNavigator} from "../Services/EntityNavigator";
import {logEntityLocatorNav} from "../Helpers/EntityLocatorNavDebug";
import {
    DEFAULT_MODULE,
    DEFAULT_PAGE,
    getModuleByName,
    MAIN_MODULES,
} from "../Constants/menus";
import Cache from "../Utilities/Cache";
import {useUserStore} from "./User";
import {useTimeTrackerStore} from "./TimeTracker";
import Utilities from "../Helpers/utilities";
import HttpStatusCodes from "../Constants/http";
import {useTeamPanel} from "./TeamPanel";
import {useActivity} from "./Activity";
import {
    applyRestoreOpenNotesFromUserState,
    resolveDocumentOpenKindFromStores,
    resolveRestoreOpenNotesPlan,
    shouldDeferRestoreOpenNotesPlan,
} from "../Helpers/restoreOpenNotesFromUserState.js";
import {hydrateActivityDrawerWidthFromUserState} from "../Components/Activity/Composables/useActivityDrawerWidth";
import {hydrateTeamPeoplePanelWidthFromUserState} from "../Components/Team/Composables/useTeamPeoplePanelWidth";
import {nextTick} from "vue";
import {setColorsToStorage} from "../Helpers/colorPalette";
import {ROUTE_NAMES} from "../Constants/routes";
import {useDivisionStore} from "./Division";
import {useRightDrawerStore} from "./RightDrawer";
import {useUnlinkedPanelState} from "./useUnlinkedPanelState";
import {TEAM_PANEL_VIEW_MODES} from "../Constants/menus";
import {resolveTeamDashboardViewBy, TEAM_DASHBOARD_VIEW_BY_DEFAULT} from "../Components/Dashboard/teamDashboardViewBy";
import {
    resolveTeamDashboardSectionFullscreen,
    toggleTeamDashboardSectionFullscreen as nextTeamDashboardSectionFullscreen,
} from "../Components/Dashboard/teamDashboardSectionFullscreen";
import {
    hydrateTeamDashboardSectionFullscreen,
    writeTeamDashboardSectionFullscreenToStorage,
} from "../Components/Dashboard/teamDashboardSectionFullscreenStorage";
import {hydrateTeamDashboardViewBy, writeTeamDashboardViewByToStorage} from "../Components/Dashboard/teamDashboardViewByStorage";
import {
    hydrateTeamDashboardScreenshotFilters,
    writeTeamDashboardScreenshotFiltersToStorage,
} from "../Components/Dashboard/teamDashboardScreenshotFiltersStorage";
import {
    WORKLINE_FILTERS,
    WORKLINE_SORT_OPTIONS,
    WORKLINE_GROUP_OPTIONS,
} from "../Constants/worklineFilter";
import {
    getShiftTimerResponseShape,
    resolveShiftTimerResponseArray,
    SHIFT_TIMER_RESPONSE_SHAPE,
} from "../Helpers/shiftTimerResponseShape";
import {
    applyLocalShiftPauseToWorkline,
    syncWorklineRowsOntoUserProfiles,
} from "../Helpers/liveSyncProfileShiftToWorkline";
import {
    normalizeWorkspaceFilterIds,
    shouldClearWorkSelectionForWorkspaceFilter,
    workspaceFilterIncludesUnmapped,
    withUnmappedIncludedInWorkspaceFilter,
} from "../Helpers/workspaceFilterSelection";
import { resolveWorklineStatusCountViewPreference } from "../Helpers/worklineStatusCountStrip";
import {toPersistedEntityId} from "../Helpers/persistedEntityId";
import {useChat} from "Admin/js/Components/Chat/Store/Chat.js";
import {shouldReseedUnreadOnConversationOpen} from "../Helpers/unreadMessages.js";

/** Matches NavigationButtons history menu and user-state `histories` / `recently_opened_conversations` batch size (30). */
export const SELECTION_HISTORY_LIMIT = 30;

export const useAppState = defineStore("AppStateStore", {
    state: () => ({
        user_id: null,
        workspace_id: null,
        board_id: null,
        item_id: null,
        document_id: null,
        active_panel: "",
        // is_team_opened:false,
        conversation_id: null,
        division_id: null,
        is_conversation_open: null,

        spaces_panel_view: "kanban",
        boards_panel_view: "kanban",
        items_panel_view: "kanban",
        team_panel_view_mode: 'dashboard',
        selected_variant_tab: "all",
        previous_selected_variant_tab: "all",
        locked_selected_variant_tab: null,
        tab: "",

        // is_document_kanban_view: null,
        is_document_open: false,

        updateStateThrottler: null,
        is_state_restoring_in_progress: 1,

        is_manage_organization_role_opened: false,
        role_object: {},
        current_division: null,

        invite_drawer: {
            visible: false,
            data: {},
            type: "item",
            new_group: false,
            is_team_opened: false,
            is_new_directory_opened: false,
            is_productivity_opened: false,
            is_workday_opened: false,
            is_workday_permission_opened: false,
        },

        productivity_drawer: {
            visible: false,
            data: {},
            type: "item",
            is_productivity_opened: false,
        },

        prospus_mouse_track_y: 0,
        prospus_mouse_track_x: 0,
        prospus_mouse_track: false,
        show_floating_buttons: true,
        user_is_typing_on_chat: false,
        page_loader: true,
        conv_loader: true,
        activity_details: false,
        selected_module: null,
        active_page: DEFAULT_PAGE,
        main_modules: MAIN_MODULES,
        activity_pinned_list: [],
        activity_expanded_list: false,
        activity_drawer_chat_preview: false,
        activity_panel_pinned: "unpinned",
        activity_drawer_width_px: null,
        team_people_panel_width_px: null,
        is_permission_error: false,
        usr_profile_logged_time: [],
        /**
         * True after `/shift-timer/shift` has been stored. Distinguishes the
         * initial empty worklog list from a successful empty payload, and stays
         * false if that request never succeeds.
         */
        usr_profile_logged_time_fetched: false,
        workline_shift_activity: null,
        /** Persisted org / filter / team count strip view (independent of workline avatar filter). */
        worklineStatusCountView: null,
        shift_timer_division_groups: [],
        /** Last argument passed to `storeShiftInUsers` (shift / workline); re-normalized when workline prefs load. */
        last_shift_timer_store_payload: null,
        handle_fetch_logged_in_time_interval: null,
        is_in_active_window: null,
        is_internet_connected: true,
        team_filter: null,
        worklineFilterDrawer: false,
        conversationHistoryDrawerOpen: false,
        activeTaskListDrawerOpen: false,
        taskBarActiveTaskListMenuOpen: false,
        shiftJournalMenuOpen: false,
        operationsTrayMenuOpen: false,
        helpMenuOpen: false,
        mainAccountMenuOpen: false,
        worklineFilterPrefetchLoading: false,
        workspace_filter: [],
        team: {
            selected_user_id: null,
            assigner_id: null,
            is_subordinate_panel: false,
            selected_subordinate_id: null,
        },
        worklineFilter: {
            selectedFilter: WORKLINE_FILTERS.EVERYONE.value,
            selectedSort: WORKLINE_SORT_OPTIONS[0].value,
            selectedGroup: "",
            selectedDivisions: [],
            expanded_division_ids: [],
        },
        worklineDirectReportProfileIds: [],
        countries: [],
        services: [],
        productive: {
            selected_user_id: null,
            selected_tab: null,
            productivity_opened: false,
            filter: {
                by_chronological: true,
                by_space: false,
                last_7_days: true,
                last_30_days: false,
                today: false,
                custom_dates: null,
            },
        },
        live_sync: {
            is_disconnected: false,
            connected_at: null,
            disconnected_at: null,
            connected_count: 0,
        },
        app_focus: {
            focus_in_at: null,
            focus_out_at: null,
        },
        saved_state: null,
        restoreSpaceStatePromise: null,
        show_control_panel: false,
        show_control_panel_active_index: 0,
        selection_histories: [],
        selection_history_index: 0,
        is_back_clicked: false,
        notification_confirm_dialog: true,
        skip_update_state: false,
        is_log_enable: false,
        in_between_restoring_status: null,
        has_opened_context_menu: false,
        message_input_height: null,
        is_agent: true,
        is_detail_mode: false,
        is_panel_loaded: false,
        is_dashboard_mode: false,
        tour_index: 0,
        contextMenuConvMap: new Map(),
        user_interaction: false,
        show_support_admin_dialog: false,
        esa_action_type: null,
        organization_timezones: [],
        ignore_cashed_module: false,
        invitation_accepted: null,
        restrictRestoreConversation: false,
        previous_route_name: null,
        skip_locate_animation: false,
        entity_navigate_with_locator: false,
        suppress_entity_locator_after_document_reload: false,
        member_select: {
            active_index: null,
            selected_profile_id: null,
        },
        import_state: null,
        is_team_panel_dashboard_mode: false,
        /** Incremented to trigger a silent team-dashboard refetch (e.g. after reports-to changes). */
        team_dashboard_refresh_nonce: 0,
        last_team_route_path: null,
        dismissed_space_creation_tooltip: false,
        dismissed_board_creation_tooltip: false,
        team_dashboard_team_expanded: true,
        team_dashboard_assigner_and_assignee_expanded: true,
        team_dashboard_effort_distribution_expanded: true,
        team_dashboard_view_by: TEAM_DASHBOARD_VIEW_BY_DEFAULT,
        team_dashboard_screenshots_expanded: true,
        team_dashboard_screenshots_wsc_installed_filter: "all",
        team_dashboard_screenshots_active_filter: "yes",
        /** `view` | `screenshots` | null — which Team Dashboard section fills the panel. */
        team_dashboard_section_fullscreen: null,
    }),
    getters: {
        getModuleWork: (state) =>
            state.main_modules.find((module) => module.name === "work"),
        getModuleActivity: (state) =>
            state.main_modules.find((module) => module.name === "activity"),
        getModuleTeam: (state) =>
            state.main_modules.find((module) => module.name === "team"),
        is_group_locked_variant_tab: (state) =>
            state.locked_selected_variant_tab === "group",
        is_group_selected_variant_tab: (state) =>
            state.selected_variant_tab === "group",
        is_invite_selected_variant_tab: (state) =>
            state.selected_variant_tab === "invites",
        is_activity: (state) => state.active_page == "activity",
        is_team: (state) => state.active_page == "team",
        is_work: (state) => state.active_page == "work",
        is_align: (state) => state.active_page == "align",
        is_settings: (state) => state.selected_module?.name === "user-settings",
        is_directory: (state) => state.selected_module?.name === "directory",
        is_productive_panel_open: (state) =>
            state.productivity_drawer?.is_productivity_opened,
        selection_history: (state) => state.selection_histories,
        is_internet_disconnected: (state) => !state.is_internet_connected,
        is_logging_started: (state) => state.is_log_enable,
        is_user_interact_with_ui: (state) => state.user_interaction,
        show_enterprice_support_dialog: (state) =>
            state.show_support_admin_dialog,
        is_reports_active: (state) => state.selected_module?.name === "reports",
        countriesMap: (state) => {
            return state.countries.reduce((acc, country) => {
                acc.set(country.id, country);
                return acc;
            }, new Map());
        },
    },
    actions: {
        setImportState(state) {
            this.import_state = state;
        },
        openMemberSelect(index) {
            this.member_select.active_index = index;
            this.member_select.selected_profile_id = null;
        },

        confirmMemberSelect(profileId) {
            this.member_select.selected_profile_id = profileId;
        },

        resetMemberSelect() {
            this.member_select.active_index = null;
            this.member_select.selected_profile_id = null;
        },

        updateDocumentState(value = false) {
            this.is_document_open = value;
            this.updateState();
        },
        setTourIndex(n) {
            this.tour_index = n;
        },

        setRoleObject(roleObject = {}) {
            this.role_object = roleObject;
        },

        setCurrentDivision(division = null) {
            this.current_division = division;
        },

        disconnected() {
            this.live_sync.is_disconnected = true;
            this.live_sync.disconnected_at = Date.now();
        },
        connected() {
            this.live_sync.is_disconnected = false;
            this.live_sync.connected_at = Date.now();
            this.live_sync.connected_count = this.live_sync.connected_count + 1;
        },
        togglePageLoader(value = null) {
            if (value === null) {
                return (this.page_loader = !this.page_loader);
            }

            this.page_loader = value;
        },
        setSelectedVariantTab(value) {
            this.selected_variant_tab = value;
        },
        setPreviousSelectedVariantTab(value) {
            this.previous_selected_variant_tab = value;
        },
        resetSelectedVariantTab() {
            this.selected_variant_tab = "all";
        },
        setTab(value) {
            this.tab = value;
        },
        handleMembershipPanel() {
            if (this.previous_selected_variant_tab !== "members") {
                this.previous_selected_variant_tab = "all";
                this.selected_variant_tab = "all";
            } else if (this.invite_drawer.is_team_opened) {
                this.selected_variant_tab = "all";
            } else {
                this.selected_variant_tab = this.previous_selected_variant_tab;
            }
        },
        setLockedSelectedVariantTab(value) {
            this.locked_selected_variant_tab = value;
        },
        setActiveMouseTrack(value) {
            this.prospus_mouse_track = value;
        },
        setActiveFloatingBtns(value) {
            this.show_floating_buttons = value;
        },
        setActiveUserTyping(value) {
            this.user_is_typing_on_chat = value;
        },
        setMouseYpath(value) {
            this.prospus_mouse_track_y = value;
        },
        setInActiveWindow(value) {
            this.is_in_active_window = value;

            if (value) {
                this.app_focus.focus_out_at = Date.now();
            } else {
                this.app_focus.focus_in_at = Date.now();
            }

            // reload if app focus after 30 minutes
            if (
                this.app_focus.focus_out_at &&
                this.app_focus.focus_in_at &&
                this.app_focus.focus_in_at - this.app_focus.focus_out_at >
                30 * 60 * 1000
            ) {
                // if focus in after 30 minutes app will reload.
                window.location.reload();
            }
        },
        async setInternetConnectivity(value) {
            try {
                await axios.get(`online-status`);
                this.is_internet_connected = true;
                useConversation().loadConversationKaamfuRequests();
                useUserStore().syncProgressesOnReconnect();
            } catch (error) {
                const offline_error_codes = [
                    "ERR_NETWORK", // No network connection
                    "ECONNABORTED", // Connection timed out
                    "ERR_CONNECTION_REFUSED", // Connection refused,
                    "ENOTFOUND", // Domain not found
                ];

                if (offline_error_codes.includes(error.code)) {
                    this.is_internet_connected = false;
                    console.log("api", this.is_internet_connected);
                }
            }
        },
        setLoggingOn(value) {
            this.is_log_enable = value;
        },
        setUserInteraction(value) {
            this.user_interaction = value;
        },
        toggleNewGroupInvite(val) {
            this.invite_drawer.new_group = val ?? !this.invite_drawer.new_group;
        },

        toggleInviteDrawer(visible, objData = {}, is_team_opened) {
            const type = objData?.type ?? useWorkspace().invite_type;
            this.invite_drawer.visible = visible ?? !this.invite_drawer.visible;
            this.invite_drawer.data = objData;
            this.invite_drawer.type = type;
            this.invite_drawer.is_team_opened = is_team_opened ?? false;
        },

        toggleNewDirectoryPanel(val) {
            this.invite_drawer.is_new_directory_opened =
                val ?? !this.invite_drawer.is_new_directory_opened;
        },

        toggleProductivityDrawer(val, is_update_state = true) {
            this.productivity_drawer.is_productivity_opened = val ?? false;

            if (!is_update_state) return;

            const productive = this.productive;
            if (productive)
                productive.productivity_opened =
                    this.productivity_drawer.is_productivity_opened;

            this.updateProductiveState(productive);
        },

        setControlAsSelectedModule(val) {
            this.show_control_panel = val;
        },
        setStoreAsSelectedModule() {
            this.selected_module = this.main_modules.find(
                (module) => module.name === "store",
            );
        },
        setWorkAsSelectedModule() {
            const module = this.main_modules.find(
                (module) => module.name === "work",
            );
            this.setSelectedModule(module);
        },
        setActivityAsSelectedModule() {
            const module = this.main_modules.find(
                (module) => module.name === "activity",
            );
            this.setSelectedModule(module);
        },
        setTeamAsSelectedModule() {
            const module = this.main_modules.find(
                (module) => module.name === "team",
            );
            this.setSelectedModule(module);
        },
        setGoalsAsSelectedModule() {
            this.selected_module = this.main_modules.find(
                (module) => module.name === "goals",
            );
        },
        setPlannerAsSelectedModule() {
            this.selected_module = this.main_modules.find(
                (module) => module.name === "planner",
            );
        },

        updateSelectedModule(name) {
            this.selected_module = getModuleByName(name);
            this.active_page = name;
        },

        setSelectedModule(data, is_restore = false) {
            if (!data) return;

            // if (!useUnlinkedPanelState().isUnlinkedPanelActive) {
            //     Utilities().toggleChatPopover(false);
            // }

            this.updateSelectedModule(data.name);

            const cache = new Cache();
            cache.set("active_page", this.active_page);

            setTimeout(() => {
                this.restoreActivityState();
                if (is_restore) {
                    useWorkspace().restoreEditorState(
                        this.document_id,
                        this.item_id,
                    );
                }
            }, 100);

            if (!is_restore) {
                useWorkspace().toggleDocument();
                this.is_document_open = false;
            }

            this.updateState();
        },
        setDrawerType(type) {
            this.invite_drawer.type = type;
        },
        openDocumentInActivity() {
            this.is_document_open = true;
            this.updateState();
        },
        closeDocumentInActivity() {
            this.is_document_open = false;
            this.updateState();
        },
        updateDocumentStateInActivity(status) {
            this.is_document_open = status;
            this.updateState();
        },
        toggleActivityDetails(is_visible = true) {
            this.activity_details = is_visible;
            this.updateState();
        },
        initializeWith(data) {
            this.startRestoringState();
            if (!this.ignore_cashed_module) {
                //this.selected_module = DEFAULT_MODULE;
                this.ignore_cashed_module = false;
            }

            this.$patch(data);
            if (this.last_shift_timer_store_payload != null) {
                this.storeShiftInUsers(this.last_shift_timer_store_payload);
            }
            if (
                data &&
                Object.prototype.hasOwnProperty.call(data, "workspace_filter")
            ) {
                this.workspace_filter = data.workspace_filter;
            }
            //console.log("after setting", this.workspace_id);

            // Skip state restoration for direct navigation routes
            if (!shouldSkipStateRestoration()) {
                this.restoreState();
            } else {
                // this is make sure all other state like conv history and task history loaded
                this.restoreSpaceState(null, 0);
            }
        },

        startRestoringState() {
            this.is_state_restoring_in_progress += 1;
        },
        stopRestoringState() {
            this.is_state_restoring_in_progress -= 1;

            /* if (this.in_between_restoring_status) {
                this.save(this.in_between_restoring_status);
            }*/
        },
        setPinnedItems(value) {
            this.activity_pinned_list = value;
        },
        async restoreState() {
            await this.restoreSpaceState();
            this.restoreConversationState();

            timeOutHelper(() => {
                this.restoreActivityState();
            }, 100);
        },
        restoreDivisionState() {
            if (
                this.active_page !== ROUTE_NAMES.DIVISION ||
                !this.division_id
            ) {
                return;
            }

            const division_store = useDivisionStore();
            const right_drawer_store = useRightDrawerStore();
            const node = division_store.getNodeById(Number(this.division_id));

            if (!node?.trackId) {
                return;
            }

            division_store.setSelectedNode(node);
            division_store.setShowMembership(false);
            right_drawer_store.openDivision();
        },
        restoreRoute(state) {
            const module = getModuleByName(state?.active_page);
            if (!module) return;

            this.setSelectedModule(module);

            // Only restore route if we have a selected module and it's not the default work module
            //if (this.selected_module && this.selected_module.name !== ROUTE_NAMES.WORK) {
            const routeName = this.selected_module.name;

            // Import router dynamically to avoid circular dependency
            import("../router").then(({default: router}) => {
                // Only navigate if we're not already on the target route
                if (router.currentRoute.value.name !== routeName) {
                    router.push({name: routeName});
                }
            });
            //}
        },
        goToHome() {
            this.goToRoute(ROUTE_NAMES.HOME);
        },
        goToWork() {
            this.goToRoute(ROUTE_NAMES.WORK);
        },
        goToActivity() {
            this.goToRoute(ROUTE_NAMES.ACTIVITY);
        },
        goToDirectory() {
            this.goToRoute(ROUTE_NAMES.DIRECTORY);
        },
        goToSettings() {
            this.goToRoute(ROUTE_NAMES.SETTINGS);
        },
        goToTeam() {
            this.goToRoute(ROUTE_NAMES.TEAM);
        },
        goToReports() {
            this.goToRoute(ROUTE_NAMES.REPORTS);
        },
        goToAlign() {
            this.goToRoute(ROUTE_NAMES.ALIGN);
        },
        goToRoute(routeName) {
            // Import router dynamically to avoid circular dependency
            import("../router").then(({default: router}) => {
                if (router.currentRoute.value.name !== routeName)
                    router.push({name: routeName});
            });
        },
        setPinnedList(activity_pinned_items) {
            const activity_pinned_list = activity_pinned_items.map((str) => {
                return Number(str);
            });

            this.setPinnedItems([...new Set(activity_pinned_list)]);
        },
        setPanelState(state) {
            const active_page = state.active_page;

            if (active_page)
                this.setSelectedModule(getModuleByName(active_page), true);
            else this.setSelectedModule(DEFAULT_MODULE, true);
        },
        applyWorklineFilterFromUserStatePreferences(workline_preferences) {
            if (
                !workline_preferences ||
                typeof workline_preferences !== "object"
            ) {
                return;
            }

            const filter = workline_preferences.filter ?? {};
            const divisionIds = filter.division_ids;
            const groupOn = WORKLINE_GROUP_OPTIONS[0]?.value ?? "";

            this.worklineFilter = {
                selectedFilter:
                    filter.selection ?? WORKLINE_FILTERS.EVERYONE.value,
                selectedSort:
                    workline_preferences.sort ?? WORKLINE_SORT_OPTIONS[0].value,
                selectedGroup: workline_preferences.group_by_division
                    ? groupOn
                    : "",
                selectedDivisions: Array.isArray(divisionIds)
                    ? divisionIds.slice()
                    : [],
                expanded_division_ids:
                    workline_preferences.expanded_division_ids ?? [],
            };

            this.worklineStatusCountView = resolveWorklineStatusCountViewPreference(
                workline_preferences.status_count_view,
                filter.selection ?? WORKLINE_FILTERS.EVERYONE.value,
            );

            if (this.last_shift_timer_store_payload != null) {
                this.storeShiftInUsers(this.last_shift_timer_store_payload);
            }
        },
        applyWorklineUpdateFromApiResponse(
            workline_preferences,
            workline,
            shift_activity = null,
        ) {
            if (workline != null || shift_activity != null) {
                this.storeShiftInUsers({
                    ...(Array.isArray(workline) ? { workline } : workline),
                    ...(shift_activity != null
                        ? { shift_activity }
                        : {}),
                });
            }

            if (workline_preferences) {
                this.applyWorklineFilterFromUserStatePreferences(
                    workline_preferences,
                );
            }
        },
        applyWorklineStatusCountViewFromApiResponse(
            workline_preferences,
            shift_activity = null,
        ) {
            if (shift_activity != null) {
                this.applyWorklineShiftActivityOnly(shift_activity);
            }

            if (workline_preferences) {
                this.worklineStatusCountView =
                    resolveWorklineStatusCountViewPreference(
                        workline_preferences.status_count_view,
                        workline_preferences.filter?.selection ??
                            this.worklineFilter.selectedFilter,
                    );
            }
        },
        applyWorklineShiftActivityOnly(shift_activity) {
            if (shift_activity == null || typeof shift_activity !== "object") {
                return;
            }

            this.workline_shift_activity = {
                organization: shift_activity.organization ?? null,
                filter: shift_activity.filter ?? null,
                team: shift_activity.team ?? null,
                shifted_in_count: shift_activity.shifted_in_count ?? 0,
                total_members_count: shift_activity.total_members_count ?? 0,
            };

            if (
                this.last_shift_timer_store_payload != null &&
                typeof this.last_shift_timer_store_payload === "object" &&
                !Array.isArray(this.last_shift_timer_store_payload)
            ) {
                this.last_shift_timer_store_payload = {
                    ...this.last_shift_timer_store_payload,
                    shift_activity,
                };
            }
        },
        async fetchWorklinePreferences() {
            try {
                const {data} = await axios.get("workline-preferences");
                const preferences = data?.data?.workline_preferences;
                if (preferences) {
                    this.applyWorklineFilterFromUserStatePreferences(
                        preferences,
                    );
                }
            } catch (error) {
                console.log("fetchWorklinePreferences failed", error);
            }
        },
        async patchWorklineExtendedDivisionIds(divisionId, is_expanded) {
            const id = Number(divisionId);
            if (!Number.isFinite(id) || typeof is_expanded !== "boolean") {
                return;
            }
            const expandedDivisionIds =
                this.worklineFilter.expanded_division_ids;
            if (is_expanded) {
                if (!expandedDivisionIds.includes(id)) {
                    expandedDivisionIds.push(id);
                }
            } else {
                this.worklineFilter.expanded_division_ids =
                    expandedDivisionIds.filter((storedId) => storedId !== id);
            }

            try {
                const {data} = await axios.patch(
                    "workline-preferences/expanded-division-ids",
                    {
                        expanded_division_ids: [
                            ...this.worklineFilter.expanded_division_ids,
                        ],
                    },
                );
            } catch (error) {
                console.log("patchWorklineExtendedDivisionIds failed", error);
            }
        },
        async restoreSpaceState(state = null, is_state_to_restore = 1) {
            // Prevent multiple simultaneous restoration calls
            if (this.restoreSpaceStatePromise) {
                return this.restoreSpaceStatePromise;
            }

            /* const is_activity = DEFAULT_MODULE.name == "activity";
            if (is_state_to_restore && !this.workspace_id && !is_activity) {
                this.stopRestoringState();
                return;
            } */
            // Set promise IMMEDIATELY to prevent race conditions
            this.restoreSpaceStatePromise = (async () => {
                //load workspace boards
                const workspace_store = useWorkspace();
                const conversation_store = useConversation();
                try {
                    if (state === null) {
                        const {data} = await axios.get(
                            `/user-states?load-space-board=1`,
                        );
                        state = data;
                    }

                    // Restore filter ids only; do not open the overlay (blocks load).
                    this.workspace_filter = state.workspace_filter;

                    // if (this.workspace_filter.length) {
                    //     useLeftDrawerStore().openDivisionSpacesFilter(250);
                    // }

                    this.applyWorklineFilterFromUserStatePreferences(
                        state?.workline_preferences,
                    );

                    workspace_store.applyPersistedViewMode(
                        useUserStore().profile?.id,
                    );

                    this.restoreProductivePanel(state.productive);
                    this.setPinnedList(state.activity_pinned_list);
                    this.activity_expanded_list = Boolean(
                        state.activity_expanded_list ?? false,
                    );
                    this.activity_drawer_chat_preview = Boolean(
                        state.activity_drawer_chat_preview ?? false,
                    );
                    this.activity_panel_pinned =
                        state.activity_panel_pinned === "pinned"
                            ? "pinned"
                            : "unpinned";
                    this.activity_drawer_width_px =
                        state.activity_drawer_width_px ?? null;
                    this.team_people_panel_width_px =
                        state.team_people_panel_width_px ?? null;
                    hydrateActivityDrawerWidthFromUserState(
                        state.activity_drawer_width_px,
                    );
                    hydrateTeamPeoplePanelWidthFromUserState(
                        state.team_people_panel_width_px,
                    );
                    this.restoreSelectionHistory(state?.histories);
                    conversation_store.restoreRecentlyOpenedConversations(
                        state?.recently_opened_conversations,
                    );
                    this.dismissed_space_creation_tooltip =
                        state.dismissed_space_creation_tooltip ?? false;
                    this.dismissed_board_creation_tooltip =
                        state.dismissed_board_creation_tooltip ?? false;

                    if (is_state_to_restore) {
                        this.updateSelectedItemId(state.item_id);
                        this.updateEditor(state.document_id);
                        this.updateConversationId(state.conversation_id);
                        this.updateConversationState(
                            state.is_conversation_open,
                        );
                        Utilities().toggleChatPopoverWithoutUpdatingState(
                            state.is_conversation_open,
                        );

                        this.updateDocumentStateInActivity(
                            state.is_document_open,
                        );
                        workspace_store.loadLastOpenedSpaceAndBoard(state);
                        workspace_store.restoreActivePanel(this.active_panel);
                        workspace_store.restoreEditorState(
                            this.document_id,
                            this.item_id,
                        );

                        const active_item = useWorkspaceTree()?.activeItem;

                        if (state?.is_link_data && active_item) {
                            workspace_store.setLocateClassName("item", null, {
                                fromRestore: true,
                            });
                        }

                        if (!this.selected_module) this.setPanelState(state);

                        if (this.is_team) {
                            this.closeDocumentInActivity(false);
                            useWorkspace().setEditorView(false);
                            useWorkspace().toggleDocument();
                        }
                        this.team_panel_view_mode = state.team_panel_view_mode;
                        this.team_dashboard_team_expanded = state.team_dashboard_team_expanded ?? true;
                        this.team_dashboard_assigner_and_assignee_expanded = state.team_dashboard_assigner_and_assignee_expanded ?? true;
                        this.team_dashboard_effort_distribution_expanded = state.team_dashboard_effort_distribution_expanded ?? true;
                        // this.team_dashboard_view_by = resolveTeamDashboardViewBy(
                        //     state.team_dashboard_view_by,
                        // );
                        this.team_dashboard_view_by = hydrateTeamDashboardViewBy(
                            useUserStore().profile?.id,
                            state.team_dashboard_view_by,
                        );
                        this.team_dashboard_screenshots_expanded = state.team_dashboard_screenshots_expanded ?? true;
                        {
                            const screenshot_filters =
                                hydrateTeamDashboardScreenshotFilters(
                                    useUserStore().profile?.id,
                                    {
                                        wsc_installed_filter:
                                            state.team_dashboard_screenshots_wsc_installed_filter,
                                        active_filter:
                                            state.team_dashboard_screenshots_active_filter,
                                    },
                                );
                            this.team_dashboard_screenshots_wsc_installed_filter =
                                screenshot_filters.wsc_installed_filter;
                            this.team_dashboard_screenshots_active_filter =
                                screenshot_filters.active_filter;
                        }
                        this.team_dashboard_section_fullscreen =
                            hydrateTeamDashboardSectionFullscreen(
                                useUserStore().profile?.id,
                                state.team_dashboard_section_fullscreen,
                            );
                        this.restoreDocumentState(state);
                    }
                } catch (error) {
                    if (error.response?.status === HttpStatusCodes.FORBIDDEN)
                        this.setPermissionError(true);
                } finally {
                    this.stopRestoringState();
                }
            })();

            return this.restoreSpaceStatePromise;
        },
        restoreConversationState() {
            if (this.restrictRestoreConversation) {
                return this.enableRestoreConversation();
            }
            this.startRestoringState();
            const conversation_store = useConversation();

            if (!this.productive?.productivity_opened)
                conversation_store.restore(
                    this.conversation_id,
                    this.is_conversation_open,
                );

            this.stopRestoringState();
        },
        restoreDocumentState(state) {
            if (state?.is_document_open) {
                const workspace_store = useWorkspace();
                const plan = resolveRestoreOpenNotesPlan(state);

                if (plan.kind === "none") {
                    return;
                }

                const runOpenNotesRestore = () => {
                    applyRestoreOpenNotesFromUserState(state, {
                        workspaceStore: workspace_store,
                        activityStore: useActivity(),
                        teamPanelStore: useTeamPanel(),
                        fallbackActivePanel: this.active_panel,
                    });
                };

                if (shouldDeferRestoreOpenNotesPlan(plan)) {
                    nextTick(runOpenNotesRestore);
                } else {
                    runOpenNotesRestore();
                }

                return;
            }

            if (!state?.document_id) {
                return;
            }

            if (this.is_team) {
                useTeamPanel().toggleDocument(
                    state.is_document_open,
                    state.conversation_id,
                );
            } else if (this.is_work) {
                nextTick(() => {
                    useWorkspace().toggleDocument(
                        true,
                        state.conversation_id,
                    );
                });
            }
        },
        updateState() {
            if (this.is_state_restoring_in_progress) {
                this.in_between_restoring_status = this.getCurrentUserState();
                return;
            }

            this.ensureUpdateStateThrottler();
            this.updateStateThrottler();
        },
        ensureUpdateStateThrottler() {
            if (this.updateStateThrottler) {
                return;
            }

            this.updateStateThrottler = throttle(
                () => {
                    this.save(this.getCurrentUserState());
                },
                400,
                {leading: true, trailing: true},
            );
        },
        flushUpdateState() {
            if (typeof this.updateStateThrottler?.cancel === "function") {
                this.updateStateThrottler.cancel();
            }

            if (this.is_state_restoring_in_progress) {
                this.in_between_restoring_status = this.getCurrentUserState();
                return;
            }

            this.save(this.getCurrentUserState());
        },
        save(state) {
            if (this.is_state_restoring_in_progress) {
                this.in_between_restoring_status = state;
                return;
            }
            if (this.is_detail_mode) {
                state.item_view_mode = "detail";
            }
            if (
                (this.saved_state &&
                    JSON.stringify(this.saved_state) ===
                    JSON.stringify(state)) ||
                this.skip_update_state
            ) {
                // data is same. therefore, api call is not required.
                return;
            }

            axios.patch("/user-states", state);
            this.saved_state = state;
        },
        getCurrentUserState() {
            const tree = useWorkspaceTree();
            const active_item = tree.activeItem;
            const active_board = tree.activeBoard;
            const active_workspace = tree.activeWorkspace;

            return {
                // workspace_id: active_workspace?.id,
                // board_id: active_board?.id,
                // item_id: active_item?.id,
                // document_id: this.document_id,
                // conversation_id: this.conversation_id,
                // division_id: this.division_id,
                workspace_id: toPersistedEntityId(active_workspace?.id),
                board_id: toPersistedEntityId(active_board?.id),
                item_id: toPersistedEntityId(active_item?.id),
                document_id: toPersistedEntityId(this.document_id),
                conversation_id: toPersistedEntityId(this.conversation_id),
                division_id: toPersistedEntityId(this.division_id),
                is_conversation_open: this.is_conversation_open,
                // is_document_kanban_view: this.is_document_kanban_view,
                active_panel: this.active_panel,
                active_page: this.active_page,
                is_document_open: this.is_document_open,
                document_open_kind: resolveDocumentOpenKindFromStores({
                    isDocumentOpen: this.is_document_open,
                    activePage: this.active_page,
                    activityStore: useActivity(),
                    workspaceStore: useWorkspace(),
                    teamPanelStore: useTeamPanel(),
                }),
                team_filter: this.team_filter,
                workspace_filter: this.workspace_filter,
                team: this.team,
                productive: this.productive,
                tour_index: this.tour_index,
                team_panel_view_mode: this.team_panel_view_mode,
                team_dashboard_team_expanded: this.team_dashboard_team_expanded,
                team_dashboard_assigner_and_assignee_expanded: this.team_dashboard_assigner_and_assignee_expanded,
                team_dashboard_effort_distribution_expanded: this.team_dashboard_effort_distribution_expanded,
                team_dashboard_view_by: this.team_dashboard_view_by,
                team_dashboard_screenshots_expanded: this.team_dashboard_screenshots_expanded,
                team_dashboard_screenshots_wsc_installed_filter:
                    this.team_dashboard_screenshots_wsc_installed_filter,
                team_dashboard_screenshots_active_filter:
                    this.team_dashboard_screenshots_active_filter,
                activity_expanded_list: this.activity_expanded_list,
                activity_drawer_chat_preview: this.activity_drawer_chat_preview,
            };
        },
        toggleWorkspaceFilterTrackId(trackId) {
            if (!trackId) return;
            const ids = [...(this.workspace_filter ?? [])];
            const idx = ids.indexOf(trackId);
            if (idx < 0) {
                ids.push(trackId);
            } else {
                ids.splice(idx, 1);
            }
            this.workspace_filter = ids;
            this.clearWorkSelectionHiddenByWorkspaceFilter();
            this.updateState();
        },
        setWorkspaceFilter(trackIds = []) {
            this.workspace_filter = normalizeWorkspaceFilterIds(
                Array.isArray(trackIds) ? trackIds : [],
            );
            this.clearWorkSelectionHiddenByWorkspaceFilter();
            this.updateState();
        },
        /**
         * When the division filter no longer includes the selected Space Group
         * (including an empty filtered list), clear Work selection and return
         * to the Spaces panel with nothing selected.
         *
         * @returns {boolean} True when selection was cleared.
         */
        clearWorkSelectionHiddenByWorkspaceFilter() {
            const workspaceTree = useWorkspaceTree();
            const workspaceStore = useWorkspace();
            const activeWorkspace = workspaceTree.activeWorkspace;

            if (
                !shouldClearWorkSelectionForWorkspaceFilter({
                    filterIds: this.workspace_filter ?? [],
                    spaceCategories: workspaceTree.spaceTree ?? [],
                    activeWorkspaceCategoryId:
                        workspaceTree.activeWorkspaceCategory?.id,
                    activeWorkspace,
                    hasActiveBoardOrItem: Boolean(
                        workspaceTree.activeBoard?.id ||
                            workspaceTree.activeItem?.id,
                    ),
                })
            ) {
                return false;
            }

            workspaceStore.clearLastClickedEntity();
            workspaceTree.resetActiveState();
            workspaceStore.resetActiveWorkspace();
            workspaceStore.activeSpaceCategory = null;
            workspaceStore.clearChinProfileIds();
            workspaceStore.openSpacesPanel();
            workspaceStore.toggleDocument();

            this.workspace_id = null;
            this.board_id = null;
            this.item_id = null;

            this.goToWork();

            return true;
        },
        /**
         * When a division filter is already applied, include `trackId` so the
         * related Space Group stays visible. No-op when the filter is empty
         * (show-all) or the id is already selected.
         *
         * @returns {boolean} True when the selection changed.
         */
        ensureWorkspaceFilterIncludes(trackId) {
            const id = Number(trackId);
            if (!Number.isFinite(id) || id <= 0) {
                return false;
            }

            const current = normalizeWorkspaceFilterIds(
                this.workspace_filter ?? [],
            );
            // Empty filter = show-all; nothing to ensure.
            if (!current.length) {
                return false;
            }

            if (current.includes(id)) {
                return false;
            }

            this.workspace_filter = [...current, id];
            this.updateState();

            return true;
        },
        /**
         * Check Unmapped when locating into a Space Group with no division.
         * No-op when the filter is empty (show-all) or Unmapped is already on.
         *
         * @returns {boolean} True when the selection changed.
         */
        ensureWorkspaceFilterIncludesUnmapped() {
            const current = normalizeWorkspaceFilterIds(
                this.workspace_filter ?? [],
            );

            if (!current.length || workspaceFilterIncludesUnmapped(current)) {
                return false;
            }

            this.workspace_filter =
                withUnmappedIncludedInWorkspaceFilter(current);
            this.updateState();

            return true;
        },
        clearWorkspaceFilter() {
            if (!this.workspace_filter?.length) {
                return;
            }
            this.workspace_filter = [];
            this.updateState();
        },
        updateWorkspaceId(id) {
            this.workspace_id = id;
            this.board_id = null;
            this.updateState();
        },
        updateBoardId(id) {
            this.board_id = id;
            this.updateState();
        },
        updateSelectedItemId(item_id) {
            this.item_id = item_id;
            this.addItemInSelectionHistory();
            this.updateState();
        },
        updateConversationId(id) {
            if (!id)
                return console.log(`Invalid converation for state restroing`);
            this.conversation_id = id;
            this.updateState();
        },
        updateDivisionId(id) {
            this.division_id = id ?? null;

            if (id == null) {
                this.flushUpdateState();
                return;
            }

            this.updateState();
        },
        updateConversationState(is_opened) {
            const was_open = this.is_conversation_open;
            this.is_conversation_open = is_opened;
            if (shouldReseedUnreadOnConversationOpen(was_open, is_opened)) {
                useConversation().active?.markAsReadIfCorrectWindowOpenForMessageRead();
            } else if (!this.is_conversation_open) {
                useChat().setTab(null);
            }
            this.updateState();
        },
        restoreSelectionHistory(histories) {
            this.selection_histories = Array.isArray(histories)
                ? histories.slice(0, SELECTION_HISTORY_LIMIT)
                : [];
        },
        updateEditor(id) {
            if (this.is_state_restoring_in_progress) return;

            this.document_id = id;
            this.updateState();
        },
        updateActivePanel(panel_view) {
            if (this.is_state_restoring_in_progress) return;

            this.active_panel = panel_view;
            this.updateState();
        },
        updateTeamFilter(filter) {
            this.team_filter = filter;
            this.updateState();
        },
        updateProductiveState(productive_state) {
            this.productive = productive_state;
            this.updateState();
        },

        updateTeam(value) {
            if (
                value["selected_user_id"] === this.team["selected_user_id"] &&
                value["is_subordinate_panel"] ===
                this.team["is_subordinate_panel"] &&
                value["selected_subordinate_id"] ===
                this.team["selected_subordinate_id"] &&
                value["assigner_id"] === this.team["assigner_id"]
            )
                return;

            this.team = value;
            this.updateState();
        },
        async restoreActivityState() {
            if (!this.is_activity) return;

            const active_conv = useConversation().active;

            if (!active_conv?.id || !this.is_conversation_open) return;

            useConversation().setDocumentByConversationId(active_conv?.id);

            if (!this.is_document_open) {
                return this.toggleActivityDetails(false);
            }

            useWorkspace().setActiveChatData(active_conv);
            this.toggleActivityDetails(true);
        },
        updateSelectionHistoryByItem(item) {
            if (!item?.id) {
                return;
            }

            this.selection_histories = this.selection_histories.map((data) => {
                if (data.id != item.id) {
                    return data;
                }
                const sameType = data.type === item.type;
                // addItemInSelectionHistory stores tasks as type "item".
                const historyItemTreeTask =
                    data.type === "item" && item.type === "task";
                if (sameType || historyItemTreeTask) {
                    return {...data, name: item.name};
                }
                return data;
            });
        },
        setBrowserHistory() {
            const url = new URL(window.location.href);
            if (!url.pathname.replace(/^\/+|\/+$/g, "")) return;

            const new_url = new URL(url.protocol + "//" + url.hostname);
            history.replaceState({}, "", new_url.toString());
        },
        setPermissionError(value) {
            this.is_permission_error = value;
        },
        flattenShiftTimerDivisionGroups(groups) {
            const rows = [];
            const seenProfileIds = new Set();
            if (!Array.isArray(groups)) {
                return rows;
            }
            for (const g of groups) {
                const candidates = [];
                if (
                    g?.head &&
                    typeof g.head === "object" &&
                    g.head.user_profile_id != null
                ) {
                    candidates.push(g.head);
                }
                for (const m of g.members ?? []) {
                    if (m?.user_profile_id != null) {
                        candidates.push(m);
                    }
                }
                for (const row of candidates) {
                    const profileId = Number(
                        row.user_profile_id ?? row.employee_id,
                    );
                    if (
                        !Number.isFinite(profileId) ||
                        seenProfileIds.has(profileId)
                    ) {
                        continue;
                    }
                    seenProfileIds.add(profileId);
                    rows.push(row);
                }
            }
            return rows;
        },
        normalizeShiftTimerResponsePayload(payload) {
            if (payload == null) {
                return {flat: [], divisionGroups: []};
            }

            const arr = resolveShiftTimerResponseArray(payload);
            const shape = getShiftTimerResponseShape(payload);
            const keepDivisionMeta = Boolean(this.worklineFilter.selectedGroup);

            if (
                shape === SHIFT_TIMER_RESPONSE_SHAPE.GROUP_BY_DIVISION &&
                arr.length > 0
            ) {
                const flat = this.flattenShiftTimerDivisionGroups(arr);
                const divisionGroups = arr.map((g) => ({
                    group_type: g.group_type,
                    division_id: g.division_id,
                    division_name: g.division_name ?? "",
                    is_expanded: Boolean(g.is_expanded),
                    head: g.head ?? null,
                    members: Array.isArray(g.members) ? g.members : [],
                }));
                return {
                    flat,
                    divisionGroups: keepDivisionMeta ? divisionGroups : [],
                };
            }

            return {flat: arr, divisionGroups: []};
        },
        storeShiftInUsers(data) {
            if (data == null) return;

            if (
                typeof data === "object" &&
                !Array.isArray(data) &&
                data.shift_activity != null
            ) {
                const shift_activity = data.shift_activity;
                this.workline_shift_activity = {
                    organization: shift_activity.organization ?? null,
                    filter: shift_activity.filter ?? null,
                    team: shift_activity.team ?? null,
                    shifted_in_count:
                        shift_activity.shifted_in_count ?? 0,
                    total_members_count:
                        shift_activity.total_members_count ?? 0,
                };
            }

            this.last_shift_timer_store_payload = data;
            const {flat, divisionGroups} =
                this.normalizeShiftTimerResponsePayload(data);
            // New array identity so the rail recomputes; API row is source of truth.
            this.usr_profile_logged_time = Array.isArray(flat) ? [...flat] : [];
            this.shift_timer_division_groups = Array.isArray(divisionGroups)
                ? [...divisionGroups]
                : [];
            this.usr_profile_logged_time_fetched = true;

            const userStore = useUserStore();
            const getProfileById = (profileId) =>
                userStore.getUserProfileById(profileId) ??
                userStore.getUserProfile(profileId);

            const timeTracker = useTimeTrackerStore();
            syncWorklineRowsOntoUserProfiles(
                this.usr_profile_logged_time,
                getProfileById,
                {
                    taskStart: timeTracker.Task_Start,
                    taskStop: timeTracker.Task_Stop,
                    taskPause: timeTracker.Task_Pause,
                    isShiftRunning: timeTracker.isShiftRunning,
                    isShiftPauseOrUnreachableOrInActive:
                        timeTracker.isShiftPauseOrUnreachableOrInActive,
                    getAssignerProfile: (id) => userStore.getUserProfile(id),
                },
            );
        },
        async fetchLoggedTime() {
            // it can be improved in future and only call if loggedin user has subordinates
            const {data} = await axios.get("/shift-timer/shift");
            this.storeShiftInUsers(data.data);

            this.startShiftFetchingTimer();
        },
        /** Workline prefs must load before shift so group-by normalization is correct. */
        async fetchWorklineThenLoggedTime() {
            await this.fetchWorklinePreferences();
            await this.fetchLoggedTime();
        },
        swapShiftToPause(user_profile_id) {
            applyLocalShiftPauseToWorkline(
                this,
                user_profile_id,
                useTimeTrackerStore().Shift_Pause,
            );
        },
        startShiftFetchingTimer() {
            if (this.handle_fetch_logged_in_time_interval) return;

            this.handle_fetch_logged_in_time_interval = setInterval(() => {
                axios.get("/shift-timer/shift").then(({data}) => {
                    this.storeShiftInUsers(data.data);
                });
            }, 30000);
        },
        stopShiftFetchingTimer() {
            clearInterval(this.handle_fetch_logged_in_time_interval);
        },
        getShiftInUsers() {
            const profiles = [];
            const userStore = useUserStore();

            this.usr_profile_logged_time.forEach((shift_in_user) => {
                const profile = userStore.getUserProfile(
                    shift_in_user.user_profile_id,
                );

                if (profile) {
                    profiles.push(profile);
                }
            });

            return profiles;
        },
        getShiftInUsersForTeamStatus() {
            return this.getShiftInUsers().filter(
                (profile) => !useUserStore().isCurrentProfile(profile.id),
            );
        },

        getShiftInUsersForTeamStatusByUserId(user_id) {
            return this.getShiftInUsers().filter(
                (profile) => profile.id != user_id,
            );
        },

        hasActiveTask(profile) {
            return profile?.active_task;
        },
        restoreProductivePanel(productive, force_open = false) {
            this.productive = productive;
            const is_open = productive?.productivity_opened || force_open;
            this.toggleProductivityDrawer(is_open, false);
            if (is_open) Utilities()?.toggleChatPopover(is_open);
        },
        async handleSharedLink(url, failCallbck = null) {
            const token = new URL(url).pathname.replace(/^\/+|\/+$/g, "");
            if (!token) return;

            try {
                // Handle /work/HANDLE-SEQUENCE format (conversation task/item links).
                // Must use locate navigation — bare router.push skips EntityNavigator
                // when already on the same URL, leaving Work Filters hiding the item.
                if (token.startsWith("work/")) {
                    const entityId = token.substring(5);
                    if (EntityResolver.isValidFormat(entityId)) {
                        // router.push(`/${token}`);
                        await RouteHelper.navigateToWorkUniversalId(entityId);
                        return;
                    }
                }

                // Handle hash-based links
                await this.handleHashBasedLink(token);
            } catch (e) {
                console.error("Failed to handle shared link:", e);
                failCallbck?.();
            }
        },
        async handleHashBasedLink(token) {
            try {
                const {data} = await axios.get(`/links/${token}`);
                const item = data.data.item;

                if (item?.sequence_number) {
                    const url = RouteHelper.getEntityUrl(item);

                    if (router.currentRoute.value.path === url) {
                        await this.handleEntityLinkAlreadyActive(item);
                        return;
                    }

                    RouteHelper.navigateToEntity(item);
                } else {
                    throw new Error("Not a valid link");
                }
            } catch (e) {
                console.error("Failed to handle  link:", e);
                throw e;
            }
        },

        async handleEntityLinkAlreadyActive(item) {
            const handle = useUserStore().profile?.organization?.handle;

            if (!handle) {
                console.warn("RouteHelper: Unable to get organization handle");
                return null;
            }

            const entity_id = `${handle}-${item.sequence_number}`;

            return await this.handleEntityLinkAlreadyActiveByUniversalId(
                entity_id,
            );
        },

        async handleEntityLinkAlreadyActiveByUniversalId(universal_id) {
            logEntityLocatorNav("appState.handleEntityLinkAlreadyActive.start", {
                universalId: universal_id,
            });

            const entityData = await EntityResolver.resolveEntity(universal_id);

            // Navigate to entity using EntityNavigator
            logEntityLocatorNav("appState.handleEntityLinkAlreadyActive.navigate", {
                universalId: universal_id,
                entityType: entityData?.entity_type,
                entityId: entityData?.entity_id,
            });

            await EntityNavigator.navigateToEntity(entityData);
        },

        addItemInSelectionHistory() {
            if (!this.selection_histories) this.selection_histories = [];

            const tree = useWorkspaceTree();
            const active_item = tree.activeItem;
            if (
                !active_item ||
                !active_item?.id ||
                this.is_state_restoring_in_progress
            ) {
                return;
            }

            const index = this.selection_histories.findIndex(
                (history) => history.id == active_item.id,
            );

            if (index === 0) {
                return;
            }

            if (index > -1) {
                this.selection_histories.splice(index, 1);
            } else if (!this.is_back_clicked) {
                this.selection_history_index = 0;
            }

            console.log("added to history", active_item.name);

            const treeItem = tree.getItemById(active_item.id);
            const workspaceForItem = treeItem
                ? tree.getWorkspaceById(
                    treeItem.workspaceId ?? treeItem.workspace_id,
                )
                : null;
            const workspace_avatar = workspaceForItem?.avatar_url;

            this.selection_histories.splice(0, 0, {
                id: active_item.id,
                name: active_item.name,
                type: active_item?.type === "task" ? "item" : active_item?.type,
                avatar_url: workspace_avatar ?? null,
            });
            if (this.selection_histories.length > SELECTION_HISTORY_LIMIT) {
                this.selection_histories = this.selection_histories.slice(
                    0,
                    SELECTION_HISTORY_LIMIT,
                );
            }
            if (
                this.selection_history_index >= this.selection_histories.length
            ) {
                this.selection_history_index = 0;
            }
            this.is_back_clicked = false;
        },
        setHistorySelectionIndexOnClickBack() {
            const history_length = this.selection_histories.length;
            const unlinkedPanelState = useUnlinkedPanelState();
            if (unlinkedPanelState.isUnlinkedPanelActive) {
                this.selection_history_index += 1;
            }
            if (this.selection_history_index >= history_length) {
                this.selection_history_index = 0;
            }
            return this.selection_history_index;
        },
        loadCountries() {
            return axios.get(`/countries`).then((res) => {
                this.countries = res.data.data.lists ?? [];
            });
        },
        loadServices() {
            return axios.get(`/services`).then((res) => {
                //user_data.value = res.data
                this.services = res.data.data.lists;
            });
        },
        forceSignUpProcessIfNotCompleted(is_sign_up_complete = null) {
            this.show_control_panel = !Boolean(is_sign_up_complete);
            this.setControlAsSelectedModule(!Boolean(is_sign_up_complete));
            if (is_sign_up_complete) {
                /* Utilities().toggleChatPopoverWithoutUpdatingState(
                    this.is_conversation_open,
                );*/
            } else {
                this.show_control_panel_active_index = 1;
            }
        },

        setIsAgent(value) {
            this.is_agent = value;
        },

        setShowSupportAdminDialog(value = true, actionType = null) {
            this.show_support_admin_dialog = value;
            this.esa_action_type = actionType;
        },

        skipUpdateState() {
            this.skip_update_state = true;
        },

        continueUpdateState() {
            this.skip_update_state = false;
        },
        setIsdetailMode() {
            this.is_detail_mode = !this.is_detail_mode;
            this.is_dashboard_mode = false;
            const data = {
                item_view_mode: this.is_detail_mode ? "detail" : "normal",
            };
            axios.patch("/user-states", data);
        },
        toggleDashBoardOrNormalMode() {
            this.is_dashboard_mode = !this.is_dashboard_mode;
        },
        toggleTeamPanelDashboardMode(mode = null) {
            if (Object.values(TEAM_PANEL_VIEW_MODES).includes(mode)) {
                this.is_team_panel_dashboard_mode =
                    mode === TEAM_PANEL_VIEW_MODES.DASHBOARD;
            } else {
                this.is_team_panel_dashboard_mode =
                    !this.is_team_panel_dashboard_mode;
            }
        },
        saveTeamPanelDashboardMode() {
            const data = {
                team_panel_view_mode: this.is_team_panel_dashboard_mode
                    ? TEAM_PANEL_VIEW_MODES.DASHBOARD
                    : TEAM_PANEL_VIEW_MODES.LIST,
            };
            this.team_panel_view_mode = data.team_panel_view_mode;
            axios.patch("/user-states", data);
        },
        toggleActivityExpandedList(value = null) {
            if (value === null) {
                this.activity_expanded_list = !this.activity_expanded_list;
            } else {
                this.activity_expanded_list = Boolean(value);
            }

            this.updateState();
        },
        toggleActivityDrawerChatPreview(value = null) {
            if (value === null) {
                this.activity_drawer_chat_preview =
                    !this.activity_drawer_chat_preview;
            } else {
                this.activity_drawer_chat_preview = Boolean(value);
            }

            this.updateState();
        },
        updateTeamDashboardTeam(val) {
            this.team_dashboard_team_expanded = val;
            this.updateState();
        },
        updateTeamDashboardAssignerAndAssignee(val) {
            this.team_dashboard_assigner_and_assignee_expanded = val;
            this.updateState();
        },
        updateTeamDashboardEffortDistribution(val) {
            this.team_dashboard_effort_distribution_expanded = val;
            this.updateState();
        },
        updateTeamDashboardViewBy(val) {
            this.team_dashboard_view_by = resolveTeamDashboardViewBy(val);
            writeTeamDashboardViewByToStorage(
                useUserStore().profile?.id,
                this.team_dashboard_view_by,
            );
            this.updateState();
        },
        updateTeamDashboardScreenshots(val) {
            this.team_dashboard_screenshots_expanded = val;
            this.updateState();
        },
        updateTeamDashboardScreenshotsWscInstalledFilter(val) {
            this.team_dashboard_screenshots_wsc_installed_filter = val ?? "all";
            writeTeamDashboardScreenshotFiltersToStorage(
                useUserStore().profile?.id,
                { wsc_installed_filter: this.team_dashboard_screenshots_wsc_installed_filter },
            );
            this.updateState();
        },
        updateTeamDashboardScreenshotsActiveFilter(val) {
            this.team_dashboard_screenshots_active_filter = val ?? "yes";
            writeTeamDashboardScreenshotFiltersToStorage(
                useUserStore().profile?.id,
                { active_filter: this.team_dashboard_screenshots_active_filter },
            );
            this.updateState();
        },
        setTeamDashboardSectionFullscreen(section) {
            const resolved = resolveTeamDashboardSectionFullscreen(section);
            this.team_dashboard_section_fullscreen = resolved;
            writeTeamDashboardSectionFullscreenToStorage(
                useUserStore().profile?.id,
                resolved,
            );
        },
        toggleTeamDashboardSectionFullscreen(section) {
            const resolved = resolveTeamDashboardSectionFullscreen(
                nextTeamDashboardSectionFullscreen(
                    this.team_dashboard_section_fullscreen,
                    section,
                ),
            );
            this.team_dashboard_section_fullscreen = resolved;
            writeTeamDashboardSectionFullscreenToStorage(
                useUserStore().profile?.id,
                resolved,
            );
        },
        dismissCreationTooltip(type) {
            const payload = {};
            if (type === "board") {
                payload.dismissed_board_creation_tooltip = true;
                this.dismissed_board_creation_tooltip = true;
            } else if (type === "space") {
                payload.dismissed_space_creation_tooltip = true;
                this.dismissed_space_creation_tooltip = true;
            }
            axios.patch("/user-states", payload);
        },
        async fetchTimezonesBasedOnCountry(countryId = null) {
            if (!countryId) {
                return [];
            }
            try {
                const url = `timezones?country_id=${countryId}`;
                const response = await axios.get(url);

                const data = response.data?.data || [];
                const timezonesData = data.map((timezone) => {
                    return {
                        label: `UTC${timezone.offset} - ${timezone.abbreviation} - ${timezone.timezone}, ${timezone.name}`,
                        value: timezone.timezone,
                        id: timezone.id,
                    };
                });
                this.setOrganizationTimezones(timezonesData);
                return timezonesData;
            } catch (error) {
                console.error("Failed to fetch timezones:", error);
            }
        },
        setOrganizationTimezones(timezones) {
            this.organization_timezones = timezones;
        },
        async getColourTheme() {
            try {
                const res = await axios.get("color-theme");
                const data = res.data?.data;
                if (data) {
                    setColorsToStorage(data);
                    return data;
                }
            } catch (err) {
            }
        },
        async saveColourTheme(payload) {
            try {
                await axios.post("color-theme", payload);
            } catch (err) {
                Utilities().showErrorSnackbar(
                    err?.response?.data?.message ??
                    "Something went wrong. Please try again later.",
                );
            }
        },
        async getInvitationAccepted() {
            try {
                const res = await axios.get(`/invitations/accepted`);
                if (res?.data?.data?.[0]) {
                    this.invitation_accepted = res?.data?.data?.[0];
                } else {
                    this.invitation_accepted = null;
                }
            } catch (error) {
                console.error("Error occurred:", error);
            }
        },
        disableRestoreConversation() {
            this.restrictRestoreConversation = true;
        },
        enableRestoreConversation() {
            this.restrictRestoreConversation = false;
        },
        getCountryById(id) {
            return this.countriesMap.get(id);
        },
        requestTeamDashboardRefresh() {
            this.team_dashboard_refresh_nonce += 1;
        },
    },
});
